import React from "react";
import { Card, BarChart, FormSelect, Loader } from "ui-kit-ck-consultant";
import { Row, Col } from "react-flexbox-grid";
import FadeIn from "react-fade-in";

import AuthContext from "../../context/AuthContext";
import axiosConfig from "../../utils/axiosConfig";
import { colors } from "../../utils/colors";

import "./home-stats.css";

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map((y) => ({
  value: y,
  text: `${y}`,
}));

// One vertical axis, starting at zero. `max` is only set for the /20 grade.
const yAxes = (max) => [
  {
    id: "A",
    type: "linear",
    position: "left",
    ticks: { min: 0, beginAtZero: true, ...(max ? { max } : {}) },
    gridLines: { drawBorder: false },
  },
];

export const sum = (arr) => arr.reduce((acc, v) => acc + (Number(v) || 0), 0);
export const lastValue = (arr) => {
  for (let i = arr.length - 1; i >= 0; i--) if (arr[i] != null) return arr[i];
  return null;
};
// Response-count-weighted mean of the monthly grades — a plain mean would let a
// near-empty month swing the headline figure.
export const weightedGrade = (grades, counts) => {
  let num = 0;
  let den = 0;
  grades.forEach((g, i) => {
    if (g != null) {
      num += g * (counts[i] || 0);
      den += counts[i] || 0;
    }
  });
  return den ? num / den : null;
};

// Headline KPIs for the /home dashboard, scoped to the logged-in company by the
// server (/api/home-stats). Renders nothing when there is no auth context (before
// login, and in the Home unit tests that mount the page without a provider).
export default class HomeStats extends React.Component {
  static contextType = AuthContext;

  constructor(props) {
    super(props);
    this.state = { isLoad: false, year: CURRENT_YEAR, data: null };
  }

  componentDidMount() {
    if (this.context && this.context.id) this.getData();
  }

  getData = () => {
    this.setState({ isLoad: true }, () => {
      axiosConfig("home-stats/get-data", { data: { year: this.state.year } })
        .then((res) => {
          this.setState({
            data: res.data && res.data.success ? res.data : null,
            isLoad: false,
          });
        })
        .catch(() => this.setState({ data: null, isLoad: false }));
    });
  };

  handleYearChange = ({ target }) => {
    this.setState({ year: parseInt(target.value, 10) }, this.getData);
  };

  render() {
    if (!this.context || !this.context.id) return null;

    const { data, isLoad, year } = this.state;
    const hasData = data && Array.isArray(data.labels) && data.labels.length > 0;

    const totalMissions = hasData ? sum(data.volume) : 0;
    const totalMissionsN1 = hasData ? sum(data.volumeN1) : 0;
    const evolution =
      totalMissionsN1 > 0
        ? Math.round(((totalMissions - totalMissionsN1) / totalMissionsN1) * 100)
        : null;
    const currentStock = hasData ? lastValue(data.stock) : null;
    const avgGrade = hasData ? weightedGrade(data.satisfaction, data.satisfactionCount) : null;

    return (
      <>
        {hasData ? (
          <FadeIn>
            <Row>
              <Col xs={12} lg={4} className="home-stats-col">
                <Card
                  className="home-stats-card"
                  title="Volume de missions reçues"
                >
                  <div className="home-stats-kpi">
                    {totalMissions.toLocaleString("fr-FR")}
                    <span className="home-stats-kpi-unit">missions en {year}</span>
                    {evolution != null ? (
                      <span
                        className={`home-stats-kpi-evol ${
                          evolution >= 0 ? "is-up" : "is-down"
                        }`}
                      >
                        {evolution >= 0 ? "▲" : "▼"} {Math.abs(evolution)}% vs {year - 1}
                      </span>
                    ) : null}
                  </div>
                  <BarChart
                    height={220}
                    digits={0}
                    isLegend={true}
                    labels={data.labels}
                    yAxes={yAxes()}
                    dataSet={[
                      {
                        type: "bar",
                        label: `${year}`,
                        data: data.volume,
                        color: colors.green[0],
                        yAxisID: "A",
                      },
                      {
                        type: "line",
                        label: `${year - 1}`,
                        data: data.volumeN1,
                        color: "#9aa0a6",
                        yAxisID: "A",
                      },
                    ]}
                  />
                </Card>
              </Col>

              <Col xs={12} lg={4} className="home-stats-col">
                <Card
                  className="home-stats-card"
                  title="Évolution du stock de dossiers"
                >
                  <div className="home-stats-kpi">
                    {currentStock != null ? currentStock.toLocaleString("fr-FR") : "—"}
                    <span className="home-stats-kpi-unit">dossiers en cours</span>
                  </div>
                  <BarChart
                    height={220}
                    digits={0}
                    isLegend={true}
                    labels={data.labels}
                    yAxes={yAxes()}
                    dataSet={[
                      {
                        type: "bar",
                        label: "Dossiers en cours (fin de mois)",
                        data: data.stock,
                        color: colors.blue[0],
                        yAxisID: "A",
                      },
                    ]}
                  />
                </Card>
              </Col>

              <Col xs={12} lg={4} className="home-stats-col">
                <Card className="home-stats-card" title="Satisfaction client">
                  <div className="home-stats-kpi">
                    {avgGrade != null ? `${avgGrade.toFixed(1)}` : "—"}
                    <span className="home-stats-kpi-unit">/ 20 en moyenne</span>
                  </div>
                  <BarChart
                    height={220}
                    digits={1}
                    isLegend={true}
                    labels={data.labels}
                    yAxes={yAxes(20)}
                    dataSet={[
                      {
                        type: "line",
                        label: "Note moyenne / 20",
                        data: data.satisfaction,
                        color: colors.purple[0],
                        yAxisID: "A",
                      },
                    ]}
                  />
                </Card>
              </Col>
            </Row>
          </FadeIn>
        ) : !isLoad ? (
          <Card className="mb-20">
            <p className="home-stats-empty">
              Aucune donnée d'activité disponible pour {year}.
            </p>
          </Card>
        ) : null}

        <Loader show={isLoad} />
      </>
    );
  }
}
